<template>
  <div class="map">
    <div ref="map-container" id="map-container" class="map__container"></div>
    <div class="map__controls map__controls--top-right">
      <div class="map__controls__group">
        <button
          class="map__controls__item"
          @click="getUserLocation"
          title="Meinen Standort verwenden"
        >
          <IconLocate aria-hidden="true" />
        </button>
      </div>
    </div>
    <div class="map__controls map__controls--bottom-right">
      <div class="map__controls__group">
        <button class="map__controls__item" @click="zoomInClickHandler" title="hineinzoomen">
          <IconPlus aria-hidden="true" />
        </button>
        <button class="map__controls__item" @click="zoomOutClickHandler" title="hinauszoomen">
          <IconMinus aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* global L */
import {
  markerBig,
  markerHome,
  markerResult,
  markerSmall,
  markerStation,
  markerStationDisabled,
} from '@/utils/markerElements'
import { toLatLngArray } from '@/utils/misc'
import { trackGa4Event } from '@/utils/tracking'
import { mapboxServiceAccepted } from '@/utils/user-centrics'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'
import mapboxglSupported from '@mapbox/mapbox-gl-supported'
import Mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import 'mapbox.js'
import 'mapbox.js/dist/mapbox.css'
import { mapGetters } from 'vuex'

import IconLocate from '@/components/icons/IconLocate.vue'
import IconMinus from '@/components/icons/IconMinus.vue'
import IconPlus from '@/components/icons/IconPlus.vue'

export default {
  components: {
    IconLocate,
    IconPlus,
    IconMinus,
  },
  emits: ['isZoomedin'],
  data() {
    return {
      mapGlSupported: true,
      mapbox: false,
      map: null,
      geocoder: null,
      mapOptions: {
        center: [13.3, 47.7],
        zoom: 6,
      },
      markers: {},
      mapLoaded: false,
      containerResizeListener: false,
      hasSearchInput: false,
      searchResultCoordinates: null,
    }
  },
  mounted() {
    if (!this.favoriteStation) this.$store.dispatch('getFavoriteStation')
    this.mapGlSupported = mapboxglSupported.supported()

    // Use nextTick to ensure DOM is ready
    this.$nextTick(() => {
      setTimeout(() => {
        this.$store.commit('setInitMap', true)
      }, 100)
    })
  },
  activated() {
    if (this.mapLoaded) {
      this.populateDataFromStore()
    } else {
      // If map isn't loaded but component is activated, try to initialize it
      this.$nextTick(() => {
        setTimeout(() => {
          if (!this.mapLoaded) {
            this.$store.commit('setInitMap', true)
          }
        }, 100)
      })
    }
  },
  beforeUnmount() {
    this.removeContainerResizeListener()

    // Add proper map cleanup
    if (this.map) {
      if (this.geocoder) {
        this.geocoder.off('result', this.onSelectResult)
        this.geocoder.off('clear', this.onClear)
      }

      // Remove markers to prevent memory leaks
      Object.keys(this.markers).forEach((type) => {
        if (Array.isArray(this.markers[type])) {
          this.markers[type].forEach((marker) => marker.remove())
        } else if (this.markers[type]) {
          this.markers[type].remove()
        }
      })

      this.markers = {}

      // Remove map event listeners
      this.map.off('load', this.onMapLoaded)
      this.map.off('moveend', this.onMoveend)

      // Remove map
      this.map.remove()
      this.map = null
      this.mapLoaded = false
    }
  },
  computed: {
    ...mapGetters([
      'sessionData',
      'activeStep',
      'userAccountInfo',
      'mapboxAccessToken',
      'mapboxJsAccessToken',
      'initMap',
      'mainPostcode',
      'userLocationCoordinates',
      'userHomeCoordinates',
      'savedStationCoordinates',
      'stationCoordinates',
      'activeStationId',
      'activeStation',
      'hoveredStation',
      'favoriteStation',
      'selectedState',
      'telephoneStationIds',
      'keineTermineStationIds',
      'unbookableStationIds',
    ]),
    isZoomedin() {
      // not showing whole Austria
      return Boolean(this.map?.getZoom() > this.mapOptions.zoom)
    },
  },
  watch: {
    sessionData() {
      if (this.sessionData) this.$store.dispatch('getFavoriteStation')
    },
    initMap(newVal) {
      console.log('initMap changed:', newVal)
      console.log('mapGlSupported:', this.mapGlSupported)
      if (newVal) this.mapGlSupported ? this.initMapGL() : this.initMapJS()
    },
    mapLoaded() {
      if (this.mapLoaded) this.populateDataFromStore()
    },
    userLocationCoordinates() {
      // wait for user to allow or block location access
      if (this.userLocationCoordinates !== null) {
        this.setUserLocation() // Priority 1
        this.getUserHome() // Priority 2
        this.getCenterMainPostcode() // Priority 3
      }
    },
    savedStationCoordinates() {
      this.jumpToCenter(this.savedStationCoordinates)
    },
    isZoomedin() {
      this.$emit('isZoomedin', this.isZoomedin)
    },
    stationCoordinates: {
      handler() {
        if (this.stationCoordinates?.length) {
          this.addStationMarkers()

          if (this.savedStationCoordinates) return

          // deselect station when out of view
          const stationInView = this.stationCoordinates.findIndex(
            (station) => station.dienststelleId === this.activeStationId
          )
          if (stationInView >= 0) {
            this.toggleActiveMarker(this.activeStationId, true) // redrawing
          } else {
            this.$store.commit('setActiveStationId', null)
            this.$store.commit('setActiveStation', null)
          }
        }
      },
      deep: true,
    },
    mainPostcode(newVal) {
      if (newVal) this.$store.dispatch('getStationCoordinates')
    },
    activeStationId(activeStationId, oldActiveStationId) {
      this.toggleActiveMarker(oldActiveStationId, false)
      this.toggleActiveMarker(activeStationId, true)
    },
    hoveredStation(hoveredStation) {
      this.toggleHighlightMarker(hoveredStation.id, hoveredStation.highlight)
    },
    telephoneStationIds: {
      handler() {
        if (this.telephoneStationIds.length) this.disableMarkers(this.telephoneStationIds)
      },
      deep: true,
    },
    keineTermineStationIds: {
      handler() {
        if (this.keineTermineStationIds.length) this.disableMarkers(this.keineTermineStationIds)
      },
      deep: true,
    },
    unbookableStationIds: {
      handler() {
        if (this.unbookableStationIds.length) this.disableMarkers(this.unbookableStationIds)
      },
      deep: true,
    },
  },
  methods: {
    initMapGL() {
      // Check if map container exists
      if (!this.$refs['map-container']) {
        console.warn('Map container reference not found')
        return
      }

      // Clean up any existing map instance
      if (this.map) {
        this.map.remove()
        this.map = null
        this.mapLoaded = false
      }

      // Clean up geocoder if it exists
      if (this.geocoder) {
        this.geocoder.off('result', this.onSelectResult)
        this.geocoder.off('clear', this.onClear)
        this.geocoder = null
      }

      // Clear search input container
      const searchInputContainer = document.querySelector('.station__search-input')
      if (searchInputContainer) {
        searchInputContainer.innerHTML = ''
      }

      this.mapbox = Mapboxgl
      this.map = new Mapboxgl.Map({
        accessToken: this.mapboxAccessToken,
        container: this.$refs['map-container'],
        style: 'mapbox://styles/oeamtcgeo1/cjdzrhx813yla2rqn53x5m7y6',
        center: this.mapOptions.center,
        zoom: this.mapOptions.zoom,
        minZoom: this.mapOptions.zoom,
        dragRotate: false,
        renderWorldCopies: false,
      })

      this.map.touchZoomRotate.disableRotation()
      this.map.on('load', this.onMapLoaded)
      this.map.on('moveend', this.onMoveend)

      this.geocoder = new MapboxGeocoder({
        accessToken: this.mapboxAccessToken,
        mapboxgl: this.mapbox,
        language: 'de-AT',
        countries: 'AT',
        limit: 4,
        flyTo: {
          zoom: 11,
        },
        marker: false,
        proximity: {
          longitude: this.mapOptions.center[0],
          latitude: this.mapOptions.center[1],
        },
        trackProximity: false,
        useBrowserFocus: true,
        filter: (item) => {
          if (item.place_type[0] === 'poi' && !item['place_name_de-AT'].includes('ÖAMTC')) {
            return false
          } else {
            return item
          }
        },
      })

      this.geocoder.on('result', this.onSelectResult)
      this.geocoder.on('clear', this.onClear)

      // Add this check to ensure the search input container exists
      if (searchInputContainer) {
        const geocoderElement = this.geocoder.onAdd(this.map)
        searchInputContainer.appendChild(geocoderElement)

        // Configure search input elements
        const clearButton = document.querySelector('.mapboxgl-ctrl-geocoder--button')
        if (clearButton) {
          clearButton.setAttribute('aria-label', 'Eingabe löschen')
          clearButton.setAttribute('title', 'Eingabe löschen')
        }

        const searchInput = document.querySelector('.mapboxgl-ctrl-geocoder--input')
        if (searchInput) {
          searchInput.setAttribute('placeholder', 'Postleitzahl oder Ort eingeben')
          searchInput.setAttribute('aria-label', 'Stützpunkt in der Nähe suchen')
        }
      } else {
        console.warn('Search input container not found')
      }
    },
    initMapJS() {
      this.mapboxServiceAccepted = mapboxServiceAccepted()

      if (this.mapboxServiceAccepted) {
        L.mapbox.accessToken = this.mapboxJsAccessToken

        this.mapbox = L.mapbox
        this.map = L.mapbox
          .map('map-container', 'mapboxjs-map', {
            zoomControl: false,
          })
          .setView(toLatLngArray(this.mapOptions.center), this.mapOptions.zoom)
          .addLayer(L.mapbox.styleLayer('mapbox://styles/oeamtcgeo1/cjdzrhx813yla2rqn53x5m7y6'))
          .addControl(
            L.mapbox.geocoderControl('mapbox.places', {
              autocomplete: true,
              keepOpen: true,
            })
          )
          .whenReady(() => {
            setTimeout(() => {
              this.onMapLoaded()
            }, 10)
          })
          .on('moveend', this.onMoveend)
      } else {
        this.$store.commit('setInitMap', false)
        this.$store.commit('setShowMapboxCookieDialog', true)
      }
    },
    onMapLoaded() {
      this.mapLoaded = true
      this.storeMapBounds()
      this.getUserLocation()
      this.addContainerResizeListener()
    },
    populateDataFromStore() {
      // cancel if normal booking
      if (this.activeStation === null) return

      this.$store.dispatch(
        'getSavedStationCoordinates',
        this.activeStation.adresse + ' ' + this.activeStation.ort + ' ' + this.activeStation.plz
      )
    },
    addContainerResizeListener() {
      this.containerResizeListener = new ResizeObserver(() => {
        if (this.mapGlSupported) {
          this.map.resize()
        } else {
          this.map.fireEvent('resize')
        }
      })
      this.containerResizeListener.observe(this.$refs['map-container'])
    },
    removeContainerResizeListener() {
      this.containerResizeListener.unobserve(this.$refs['map-container'])
    },
    getUserLocation() {
      navigator.geolocation?.getCurrentPosition(
        (pos) => {
          this.$store.commit('setUserLocationCoordinates', [
            pos.coords.longitude,
            pos.coords.latitude,
          ])
        },
        (error) => {
          this.$store.commit('setUserLocationCoordinates', [])
          console.error(error)
        }
      )
    },
    setUserLocation() {
      if (!this.userLocationCoordinates.length) return
      this.addMarker('userLocation')

      if (this.hasSearchInput) return
      this.$store.dispatch('getPostcode', this.userLocationCoordinates)

      if (!this.savedStationCoordinates) this.jumpToCenter(this.userLocationCoordinates)
    },
    async getUserHome() {
      if (!this.userAccountInfo.address.zip) return
      await this.$store.dispatch('getUserHomeCoordinates')
      this.addMarker('userHome')

      if (this.userLocationCoordinates.length) return
      if (this.hasSearchInput) return
      this.$store.commit('setMainPostcode', this.userAccountInfo.address.zip)

      if (!this.savedStationCoordinates) {
        this.jumpToCenter([this.userHomeCoordinates[0], this.userHomeCoordinates[1]])
      }
    },
    getCenterMainPostcode() {
      if (this.savedStationCoordinates) return
      if (this.userLocationCoordinates.length) return
      if (this.userAccountInfo.address.zip) return
      if (this.hasSearchInput) return

      this.$store.dispatch('getPostcode', this.mapOptions.center)
    },
    storeMapBounds() {
      const mapBounds = this.map.getBounds()
      this.$store.commit('setMapBounds', mapBounds)
    },
    jumpToCenter(coordinates) {
      if (this.mapGlSupported) {
        this.map.flyTo({
          center: coordinates,
          zoom: 11,
          animate: false,
        })
      } else {
        this.map.setView(toLatLngArray(coordinates), 12, {
          animate: false,
        })
      }
    },
    zoomInClickHandler() {
      this.map.zoomIn()
    },
    zoomOutClickHandler() {
      this.map.zoomOut()
    },
    async onMoveend() {
      this.storeMapBounds()
      if (this.markers.stations) {
        await this.removeMarkers('stations')
      }
      if (this.activeStep !== 3) return // prevent dispatching getStationCoordinates in other steps, which will cause activeStation to be null
      if (this.mainPostcode) this.$store.dispatch('getStationCoordinates')
    },
    onSelectResult(e) {
      console.log('Geocoder result selected:', e)
      this.hasSearchInput = true
      if (this.markers.searchResult) this.removeMarkers('searchResult')

      this.searchResultCoordinates = e.result.center
      this.addMarker('searchResult')
      this.jumpToCenter(this.searchResultCoordinates)

      // Update map bounds immediately after jumpToCenter (animate: false)
      // so that subsequent getStationCoordinates calls use the correct bounds
      this.storeMapBounds()

      // search input is a postcode
      if (e.result.id.includes('postcode')) {
        this.$store.commit('setMainPostcode', e.result.text)
      } else {
        const postcode = e.result.context.find((obj) => obj.id.includes('postcode'))?.text

        if (postcode) {
          this.$store.commit('setMainPostcode', postcode)
        } else {
          // eg. search input is a place
          this.$store.dispatch('getPostcode', e.result.center)
        }
      }

      // User seaches for a location or zip code
      trackGa4Event({
        ga4Event: 'tvg_search',
        ga4ParameterName: 'search_term',
        ga4ParameterValue: e.result.text,
      })
    },
    onClear() {
      this.hasSearchInput = false
      this.removeMarkers('searchResult')

      if (this.userLocationCoordinates.length) {
        this.$store.dispatch('getPostcode', this.userLocationCoordinates)
      } else if (this.userAccountInfo.address.zip) {
        this.$store.commit('setMainPostcode', this.userAccountInfo.address.zip)
      } else {
        this.$store.dispatch('getPostcode', this.mapOptions.center)
      }
    },
    addStationMarkers() {
      this.stationCoordinates.forEach((station) =>
        this.addMarker(
          'stations',
          [station.longitude, station.latitude],
          station.dienststelleId,
          station.bezeichnung,
          station.globalStpId === this.favoriteStation?.id,
          this.selectedState.id === station.bundeslandId,
          this.telephoneStationIds.includes(station.dienststelleId),
          this.keineTermineStationIds.includes(station.dienststelleId),
          this.unbookableStationIds.includes(station.dienststelleId)
        )
      )
    },
    addMarker(
      type,
      coordinates = [],
      id,
      name,
      isFavourite = false,
      inState = true,
      telephoneOnly = false,
      keineTermine = false,
      unbookable = false
    ) {
      let icon = document.createElement('div')

      if (type === 'userLocation') {
        coordinates = this.userLocationCoordinates
        icon.className = 'marker marker--user-location'
      } else if (type === 'userHome') {
        coordinates = this.userHomeCoordinates
        icon.className = 'marker marker--poi marker--poi--userHome'
        icon.innerHTML = this.generatePoiMarkerContent('marker-home')
      } else if (type === 'searchResult') {
        coordinates = this.searchResultCoordinates
        icon.className = 'marker marker--poi marker--poi--searchResult'
        icon.innerHTML = this.generatePoiMarkerContent('marker-result')
      } else if (type === 'stations') {
        icon.className = `marker marker--poi ${
          !inState || telephoneOnly || keineTermine || unbookable ? 'is-marker-disabled' : ''
        }`
        icon.innerHTML = this.generatePoiMarkerContent(
          'marker-station',
          name,
          inState,
          telephoneOnly,
          keineTermine,
          unbookable
        )

        if (isFavourite) {
          icon.classList.add('marker--poi--star')
        }
      }

      if (this.mapGlSupported) {
        icon.addEventListener('click', this.markerClickHandler, false)
        icon.addEventListener('mouseenter', this.markerMouseEnterHandler, false)
        icon.addEventListener('mouseleave', this.markerMouseLeaveHandler, false)
      }

      // Add z-index based on marker coordinate so the lower one always shows up on top of the higher one
      icon.style.zIndex = 100000 - parseInt(coordinates[1] * 1000)

      let addedLeafletMarker
      if (type === 'userLocation' || type === 'userHome' || type === 'searchResult') {
        if (this.markers[type]) {
          if (this.mapGlSupported) {
            this.markers[type].setLngLat(coordinates)
          } else {
            this.markers[type].setLatLng(toLatLngArray(coordinates))
            addedLeafletMarker = this.markers[type]
          }
        } else {
          if (this.mapGlSupported) {
            let marker = new Mapboxgl.Marker(icon).setLngLat(coordinates).addTo(this.map)
            this.markers[type] = marker
          } else {
            const reversedCoordinates = toLatLngArray(coordinates)
            let marker = L.marker(toLatLngArray(reversedCoordinates), {
              icon: L.divIcon({
                html: icon.outerHTML,
                className: 'marker-wrapper',
                iconSize: L.point(1, 1),
              }),
            })
              .setLatLng(reversedCoordinates)
              .addTo(this.map)
            addedLeafletMarker = marker
            this.markers[type] = marker
          }
        }
      } else {
        if (!this.markers[type]) {
          this.markers[type] = []
        }

        let mapboxMarker
        if (this.mapGlSupported) {
          mapboxMarker = new Mapboxgl.Marker(icon).setLngLat(coordinates).addTo(this.map)
        } else {
          const reversedCoordinates = toLatLngArray(coordinates)
          mapboxMarker = L.marker(reversedCoordinates, {
            icon: L.divIcon({
              html: icon.outerHTML,
              className: 'marker-wrapper',
              iconSize: L.point(1, 1),
            }),
          })
            .setLatLng(reversedCoordinates)
            .addTo(this.map)
          addedLeafletMarker = mapboxMarker
        }

        icon.id = id
        mapboxMarker.id = id

        if (isFavourite) {
          mapboxMarker.isFavourite = true
        }

        this.markers[type].push(mapboxMarker)
      }

      if (!this.mapGlSupported && addedLeafletMarker) {
        addedLeafletMarker.addEventListener('click', this.markerClickHandler, false)
        addedLeafletMarker.addEventListener('mouseover', this.markerMouseEnterHandler, false)
        addedLeafletMarker.addEventListener('mouseout', this.markerMouseLeaveHandler, false)
      }
    },
    removeMarkers(type) {
      if (type === 'stations') {
        this.markers.stations.forEach((marker) => {
          if (this.mapGlSupported) {
            const markerElement = marker.getElement()
            markerElement.removeEventListener('click', this.markerClickHandler)
            markerElement.removeEventListener('mouseenter', this.markerMouseEnterHandler)
            markerElement.removeEventListener('mouseleave', this.markerMouseLeaveHandler)
          } else {
            marker.removeEventListener('click', this.markerClickHandler)
            marker.removeEventListener('mouseover', this.markerMouseEnterHandler)
            marker.removeEventListener('mouseout', this.markerMouseLeaveHandler)
          }

          marker.remove() // remove from map
        })

        delete this.markers.stations // remove from state
      } else {
        this.markers[type].remove()
        delete this.markers[type]
      }
    },
    disableMarkers(ids) {
      const markers = this.markers.stations?.filter((marker) => ids.includes(marker.id))

      markers?.forEach((marker) => {
        const markerElement = this.mapGlSupported
          ? marker.getElement()
          : marker.getElement().querySelector('.marker')
        markerElement.classList.add('is-marker-disabled')
        markerElement.getElementsByClassName('marker__poi-inner__symbol')[0].innerHTML =
          markerStationDisabled
        markerElement
          .getElementsByClassName('marker__poi-label')[0]
          .classList.add('marker__poi-label--disabled')
      })
    },
    generatePoiMarkerContent(type, name, inState, telephoneOnly, keineTermine, unbookable) {
      const disabled = !inState || telephoneOnly || keineTermine || unbookable
      let markerIcon
      if (type === 'marker-station') {
        markerIcon = disabled ? markerStationDisabled : markerStation
      } else if (type === 'marker-home') {
        markerIcon = markerHome
      } else if (type === 'marker-result') {
        markerIcon = markerResult
      }

      return `
        <div class="marker__poi-inner">
          <div class="marker__poi-inner__marker">${markerSmall}</div>
          <div class="marker__poi-inner__symbol">
            ${markerIcon}
          </div>
        </div>
        ${
          type === 'marker-station'
            ? disabled
              ? `<div class="marker__poi-label marker__poi-label--disabled">
                <p class="marker__poi-label--title">${name}</p>
                <p class="marker__poi-label--subtitle">
                  ${
                    !inState
                      ? 'Nicht im gewählten Bundesland'
                      : unbookable
                      ? 'Der Stützpunkt bietet diese Dienstleistung nicht an'
                      : keineTermine
                      ? 'Keine Termine verfügbar'
                      : 'Terminvereinbarung nur telefonisch'
                  }
                </p>
              </div>`
              : `<div class="marker__poi-label">
                <p class="marker__poi-label--title">${name}</p>
              </div>`
            : ''
        }
      `
    },
    markerClickHandler(e) {
      let markerId

      if (this.mapGlSupported) {
        e.stopPropagation()
        markerId = +e.currentTarget.id
      } else {
        markerId = +e.target.id
      }

      const markerElement = this.mapGlSupported
        ? e.currentTarget
        : e.target.getElement().querySelector('.marker')
      if (markerElement.className.includes('is-marker-disabled')) return false // disabled marker
      if (this.unbookableStationIds.includes(markerId)) return false // unbookable marker
      if (!markerId) return false // home marker

      if (this.activeStationId === markerId) {
        this.$store.commit('setActiveStationId', null)
      } else {
        this.$store.commit('setActiveStationId', markerId)
      }

      if (this.map.getZoom() < 11 || this.stationCoordinates?.length > 5) {
        const station = this.stationCoordinates.find(
          (station) => station.dienststelleId === markerId
        )
        this.jumpToCenter([station.longitude, station.latitude])
      }
    },
    markerMouseEnterHandler(e) {
      let target

      if (this.mapGlSupported) {
        e.stopPropagation()
        target = e.currentTarget
      } else {
        target = e.target
      }

      if (target.highlight === 'true' || !target.id) return false
      this.$store.commit('setHoveredStation', {
        id: +target.id,
        highlight: true,
      })
      this.toggleHighlightMarker(target.id, true)
    },
    markerMouseLeaveHandler(e) {
      let target

      if (this.mapGlSupported) {
        e.stopPropagation()
        target = e.currentTarget
      } else {
        target = e.target
      }

      if (!target.id) return false // home marker
      this.$store.commit('setHoveredStation', {
        id: +target.id,
        highlight: false,
      })
      this.toggleHighlightMarker(target.id, false)
    },
    toggleHighlightMarker(id, highlight = false) {
      const markers = this.markers.stations?.filter((marker) => marker.id === +id)
      if (markers?.length) {
        markers.forEach((marker) => {
          if (highlight && marker.isHighlight) return false
          const markerElement = this.mapGlSupported
            ? marker.getElement()
            : marker.getElement().querySelector('.marker')
          if (highlight) {
            markerElement.classList.add('is-marker-highlight')
          } else {
            markerElement.classList.remove('is-marker-highlight')
          }
          marker.isHighlight = highlight

          if (marker.isActive) return false

          this.changeMarkerIcon(highlight, markerElement)
        })
      }
      return false
    },
    toggleActiveMarker(id, activate = true) {
      const markers = this.markers.stations?.filter((marker) => marker.id === +id)

      if (markers?.length) {
        markers.forEach((marker) => {
          const markerElement = this.mapGlSupported
            ? marker.getElement()
            : marker.getElement().querySelector('.marker')
          if (activate) {
            markerElement.classList.add('is-marker-active')
          } else {
            markerElement.classList.remove('is-marker-active')
          }
          marker.isActive = activate

          if (marker.isHighlight) return false

          this.changeMarkerIcon(activate, markerElement)
        })
      }
    },
    changeMarkerIcon(big = true, markerElement) {
      const markerEl = markerElement.querySelector('.marker__poi-inner__marker')
      if (!markerEl) return false

      const icon = big ? markerBig : markerSmall

      markerEl.innerHTML = icon
    },
    // Add this method to safely check and focus the input
    checkInput() {
      return document.querySelector('.mapboxgl-ctrl-geocoder--input') !== null
    },

    focusInput() {
      const input = document.querySelector('.mapboxgl-ctrl-geocoder--input')
      if (input) {
        input.focus()
        return true
      }
      return false
    },
  },
}
</script>

<style lang="scss">
.map {
  height: 100%;
  width: 100%;

  &__container,
  .mapboxgl-canvas {
    height: 100%;
    width: 100%;
    &:focus {
      border: 2px solid $color-info-primary;
    }
  }

  &__controls {
    position: absolute;
    z-index: $zindex-map-controls;

    &--top-right {
      top: 12px;
      right: 12px;

      @media (max-width: $breakpoint-max-mobile) {
        top: 12px;
        right: 16px;
      }
    }

    &--bottom-right {
      bottom: 12px;
      right: 12px;

      @media (max-width: $breakpoint-max-mobile) {
        display: none;
      }
    }

    &__group {
      border-radius: 10px;
      box-shadow: 0 2px 4px -1px rgba(0, 0, 0, 0.2), 0 1px 14px 0 rgba(0, 0, 0, 0.12),
        0 5px 8px 0 rgba(0, 0, 0, 0.14);
    }

    &__item {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 42px;
      height: 42px;
      background: $color-yellow-primary;
      transition: background 0.2s cubic-bezier(0.23, 1, 0.32, 1);
      cursor: pointer;

      &:first-of-type {
        border-top-right-radius: 10px;
        border-top-left-radius: 10px;
      }
      &:last-of-type {
        border-bottom-left-radius: 10px;
        border-bottom-right-radius: 10px;
      }

      &:hover,
      &:focus-visible {
        background-color: $color-yellow-dark;
      }
    }
  }
}

.mapboxgl-ctrl-geocoder {
  box-shadow: none;
  height: 46px;
  width: auto;
  max-width: 350px;
  position: relative;
  z-index: $zIndex-geocoder;

  &--icon-search {
    top: 50%;
    left: unset;
    right: 10px;
    transform: translateY(-50%);
    z-index: $zIndex-geocoder-icons;
  }

  &--button:not(:disabled):hover {
    background-color: white !important;
  }

  .mapboxgl-ctrl-geocoder--pin-right {
    > * {
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      margin: 0;
      z-index: $zIndex-geocoder-icons;
    }

    svg {
      margin: 0;
      height: 18px;
      width: 18px;
    }
  }

  &--input {
    background-color: white;
    border: 1px solid $color-grey-darker;
    border-radius: 4px;
    color: $color-text-primary;
    font-family: $font-family;
    font-size: 16px;
    line-height: 1.38;
    height: fit-content;
    padding: 11px 30px 11px 12px;
    position: absolute;
    z-index: $zIndex-geocoder-input;

    @media (max-width: $breakpoint-max-mobile) {
      border: none;
      box-shadow: 0 4px 8px rgb(0 0 0 / 30%);
    }

    &::placeholder {
      color: $color-text-secondary;
    }

    &:hover:not(:focus) {
      border-color: $color-text-primary;
    }

    &:focus {
      border-color: $color-info-primary;
      outline: none;
    }
  }

  .suggestions {
    border: 1px solid $color-info-primary;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12),
      0 6px 10px 0 rgba(0, 0, 0, 0.14);
    font-family: $font-family;
    font-size: 16px;
    padding: 70px 0 0;
    top: -12px;
    left: -12px;
    max-height: 398px;
    width: calc(100% + 24px);

    .active {
      background-color: $color-grey-light;

      &:hover {
        background-color: $color-grey-light;
      }

      a {
        background-color: unset;

        &:hover {
          background-color: unset;
        }
      }
    }

    li {
      padding: 0 12px;

      &:hover {
        background-color: rgba($color-grey-light, 0.3);
      }

      a {
        border-top: 1px solid $color-grey-light;
        padding: 12px 0;

        &:hover {
          background-color: unset;
        }
      }
    }
  }
}

.marker {
  $block: &;

  &.is-marker-highlight,
  &.is-marker-active {
    z-index: $zindex-marker-active;

    .marker__poi-label {
      display: block;
    }
  }

  &.is-marker-disabled {
    cursor: default;

    .pin-border {
      fill: $color-grey-dark;
    }
  }

  .marker-wrapper & {
    top: 0;
    left: 0;

    svg {
      position: static !important;
    }

    &--user-location {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  &--user-location {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 29px;
    height: 29px;
    background-color: rgba($color-info-primary, 0.15);
    border-radius: 100%;
    cursor: default;

    &:before {
      content: '';
      display: block;
      width: 13px;
      height: 13px;
      background-color: $color-info-primary;
      border-radius: 100%;
    }
  }

  &--poi {
    position: absolute;
    height: 4px;
    width: 7px;
    cursor: pointer;

    &--userHome {
      cursor: default;
    }

    &--searchResult {
      cursor: default;

      .pin-background,
      .pin-border {
        fill: $color-yellow-primary;
      }
    }

    &--star {
      #{$block}__poi-inner {
        &::before {
          content: '';
          position: absolute;
          z-index: $zindex-marker-star;
          left: 57%;
          bottom: 70%;
          width: 20px;
          height: 20px;
          background: contain center no-repeat;
          background-size: 100%;
          background-image: url('~@/assets/images/marker-star-badge.svg');
        }
      }

      &.is-marker-active,
      &.is-marker-highlight {
        #{$block}__poi-inner {
          &::before {
            width: 26px;
            height: 26px;
            left: 65%;
            bottom: 75%;
          }
        }
      }
    }
  }

  &__poi-inner {
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    line-height: 1;
    filter: drop-shadow(0px 3px 2px rgba(0, 0, 0, 0.4));

    svg {
      display: block;
      pointer-events: none;
    }

    &__symbol {
      position: absolute;
      width: 22px;
      height: 22px;
      left: 50%;
      transform: translateX(-50%);
      bottom: 17px;

      .is-marker-highlight &,
      .is-marker-active & {
        width: 37px;
        height: 37px;
        bottom: 31px;
      }

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }

  &__poi-label {
    display: none;
    background-color: white;
    border-radius: 4px;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12),
      0 6px 10px 0 rgba(0, 0, 0, 0.14);
    font-family: $font-family;
    font-weight: $font-weight-medium;
    text-align: center;
    padding: 2px 8px;
    width: fit-content;
    position: absolute;
    top: 5px;
    left: 50%;
    transform: translateX(-50%);

    p {
      font-size: 12px;
      line-height: 1.3;
    }

    &--disabled {
      color: $color-text-secondary;
    }

    &--title {
      margin: 0 !important;
    }

    &--subtitle {
      font-weight: $font-weight-regular;
      margin: 12px 0 0 !important;
    }
  }
}
</style>
